import { getBackendUrl } from "@Utils";
/* eslint no-sequences: 0 */

import { base64StringToBlob, blobToArrayBuffer } from "blob-util";
import { convertDate } from "./luxonHelper";
import axiosWrapper from "./axiosWrapper";
import { v4 as uuid } from "uuid";
import JSZip from "jszip";
import type { GetIssueViewpointDataResponse } from "@ServerTypes/issues/issues.schema";
import type { GetFileUrlResponse } from "@ServerTypes/fileUpload/fileUpload.schema";

/**
 * SCHEMA GAP: `getIssueViewpointDataResponse` is the shared `issueSingleResponse`, whose `result` is
 * the generic issue item and declares neither the `bcfData` blob this route actually returns nor the
 * fact that a `status: true` answer always carries a row. Both are stated here, at the one call site
 * that reads them, instead of being asserted away at the read.
 */
type IssueViewpointDataResponse = GetIssueViewpointDataResponse & {
  result: NonNullable<GetIssueViewpointDataResponse["result"]> & { bcfData: string };
};

/** One camera block of a saved BCF viewpoint. */
interface BcfCamera {
  cameraViewPoint: Record<string, number>;
  cameraDirection: Record<string, number>;
  cameraUpVector: Record<string, number>;
  viewToWorldScale?: number;
}

/** The parsed `bcfData` JSON of an issue or comment viewpoint. */
interface BcfViewpointJson {
  _components: {
    defaultVisibility: boolean;
    visibilityExceptions: Record<string, { ifcGuid: string }>;
    selection: Record<string, { ifcGuid: string }>;
  };
  _orthogonalCamera?: BcfCamera;
  _perspectiveCamera?: BcfCamera;
}

/** One comment on an issue. The three `*Key` fields are generated here, not stored server-side. */
interface BcfComment {
  _id?: string;
  title?: string;
  createdDate?: number;
  createdBy?: { fullName?: string };
  bcfData?: string;
  snapshot?: string;
  snapshotKey?: string;
  bcfDataKey?: string;
  viewpointKey?: string;
}

/**
 * One issue in the export map. The index signature mirrors the caller's own declaration
 * (`BcfIssueEntry` in DesignViewer), which is what makes the per-read assertions below legal.
 */
interface BcfIssue {
  id?: string;
  title?: string;
  description?: string;
  createdDate?: number;
  createdByName?: string;
  dueDate?: number;
  snapshot?: string;
  statusInReview?: { name?: string };
  /**
   * Declared as the comment shape the export actually needs (the caller types it as an open record).
   * The three `*Key` fields are stamped onto each entry in `bcfConverter` before `getMarkup` reads them.
   */
  comments?: Record<string, BcfComment>;
  [key: string]: unknown;
}

/**
 * `Object(value)` verbatim, typed: objects pass through unchanged, `null`/`undefined` become `{}`,
 * and primitives box — a boxed string exposes index keys, whose numeric tags the regex at the end of
 * `OBJtoXML` strips again. Declaring the result instead of asserting it keeps the walk below typed.
 */
function asWalkableObject(value: unknown): Record<string, unknown> {
  const boxed: Record<string, unknown> = Object(value);
  return boxed;
}

// `obj` is arbitrary parsed JSON walked with `for...in`, so every `obj[prop]` read is `unknown` and
// is narrowed at the branch that consumes it rather than asserted into shape.
function OBJtoXML(obj: Record<string, unknown>) {
  let xml = "";
  for (const prop in obj) {
    const value = obj[prop];
    const isArrayValue = Array.isArray(value);
    xml += isArrayValue ? "" : `<${prop}>`;
    if (isArrayValue) {
      for (const item of value) {
        xml += `<${prop}>`;
        xml += OBJtoXML(asWalkableObject(item));
        xml += `</${prop}>`;
      }
    } else if (typeof value === "object") {
      // `typeof null === "object"` too; `asWalkableObject` turns it into the empty walk the previous
      // `new Object(null)` produced.
      xml += OBJtoXML(asWalkableObject(value));
    } else {
      xml += String(value);
    }
    xml += isArrayValue
      ? ""
      : `</${prop
          .replace(/\sIfcGuid=".*"/g, "")
          .replace(/\sGuid=".*"/g, "")
          .replace(/\sDefaultVisibility=".*"/g, "")}>`;
  }
  xml = xml.replace(/<\/?[0-9]{1,}>/g, "");
  return xml;
}

function downloadBcf(data: Blob, filName: string) {
  // `window.webkitURL` was the pre-2012 Safari/Android alias; `window.URL` is universal in every
  // browser this app supports, and lib.dom types both as always present.
  const url = window.URL;
  const link = url.createObjectURL(data);
  const aAttr = document.createElement("a");
  aAttr.setAttribute("download", filName);
  aAttr.setAttribute("href", link);
  document.body.appendChild(aAttr);
  aAttr.click();
  document.body.removeChild(aAttr);
}

// `unknown`, not `string`: IssuesInReview feeds this a `_.get(..., "title", "")` result, which
// lodash types as `unknown` over a typed object.
export function htmlToText(html: unknown) {
  const temp = document.createElement("div");
  // Only a string is markup. Anything else the callers hand over — an absent description/title above
  // all — contributes no text; assigning it raw used to render the literal "undefined" into the BCF.
  temp.innerHTML = typeof html === "string" ? html : "";
  return temp.textContent;
}

async function getSnapshot(src: string) {
  src = src.replace("data:image/png;base64,", "");
  const arrayBuffer = await blobToArrayBuffer(base64StringToBlob(src));
  return new Uint8Array(arrayBuffer);
}

function getViewpoint(data: BcfViewpointJson) {
  const viewpointVisibility = `Visibility DefaultVisibility="${data._components.defaultVisibility}"`;
  const viewpointExceptions = Object.keys(data._components.visibilityExceptions)
    .map((val: string) => `Component IfcGuid="${data._components.visibilityExceptions[val]!.ifcGuid}"`)
    .reduce((res: Record<string, object>, key) => ((res[key] = {}), res), {});

  const selectionExceptions = Object.keys(data._components.selection)
    .map((val: string) => `Component IfcGuid="${data._components.selection[val]!.ifcGuid}"`)
    .reduce((res: Record<string, object>, key) => ((res[key] = {}), res), {});

  let cameraType = data._orthogonalCamera;
  if (!data._orthogonalCamera) {
    cameraType = data._perspectiveCamera;
  }

  // KNOWN BUG, preserved verbatim: a viewpoint carrying neither camera throws on the next line.
  // The `!`s are erased; a guard would change behaviour.
  const cameraViewPoint = Object.keys(cameraType!.cameraViewPoint).reduce(
    (res: Record<string, number | undefined>, key) => {
      res[key.toUpperCase()] = cameraType!.cameraViewPoint[key];
      return res;
    },
    {},
  );

  const cameraDirection = Object.keys(cameraType!.cameraDirection).reduce(
    (res: Record<string, number | undefined>, key) => {
      res[key.toUpperCase()] = cameraType!.cameraDirection[key];
      return res;
    },
    {},
  );

  const cameraUpVector = Object.keys(cameraType!.cameraUpVector).reduce(
    (res: Record<string, number | undefined>, key) => {
      res[key.toUpperCase()] = cameraType!.cameraUpVector[key];
      return res;
    },
    {},
  );

  const viewpoint = {
    VisualizationInfo: {
      Components: {
        ViewSetupHints: {},
        Selection: selectionExceptions,
        [viewpointVisibility]: {
          Exceptions: viewpointExceptions,
        },
        Coloring: {},
      },
      OrthogonalCamera: {
        CameraViewPoint: cameraViewPoint,
        CameraDirection: cameraDirection,
        CameraUpVector: cameraUpVector,
        ViewToWorldScale: cameraType!.viewToWorldScale,
      },
    },
  };
  return OBJtoXML(viewpoint);
}

function getMarkup(issues: BcfIssue, comments: Record<string, BcfComment>) {
  // `Markup` is built up with computed `Topic Guid="..."` / `Comment Guid="..."` keys below, so the
  // declared shape needs the index signature the literal cannot infer. Annotation only.
  const markupJson: { Markup: { Header: Record<string, unknown>; [key: string]: unknown } } = {
    Markup: {
      Header: {},
    },
  };

  const topicKey = `Topic Guid="${issues.id}" TopicStatus="${issues.statusInReview?.name}"`;
  markupJson.Markup[topicKey] = {
    Title: issues.title,
    CreationDate: convertDate(Number(issues.createdDate), "standardIsoFormat"),
    CreationAuthor: issues.createdByName,
    Description: htmlToText(issues.description),
    DueDate: issues.dueDate ? convertDate(Number(issues.dueDate), "standardIsoFormat") : null,
  };
  const currentIssuesComments = Object.keys(comments).reduce(
    (res: Record<string, BcfComment | undefined>, key) => ((res[key] = comments[key]), res),
    {},
  );

  // KNOWN BUG, preserved verbatim: `createdBy` is dereferenced without a guard below, and the
  // keys come straight from `Object.keys` of this very map. The `!`s are erased.
  Object.keys(currentIssuesComments).forEach((commentValue: string) => {
    const viewPointKey = `Viewpoint Guid="${currentIssuesComments[commentValue]!.snapshotKey}"`;
    const viewPointsKey = `Viewpoints Guid="${currentIssuesComments[commentValue]!.snapshotKey}"`;
    const commentKey = `Comment Guid="${currentIssuesComments[commentValue]!._id}"`;

    markupJson.Markup[commentKey] = {
      Date: convertDate(Number(currentIssuesComments[commentValue]!.createdDate), "standardIsoFormat"),
      Author: currentIssuesComments[commentValue]!.createdBy!.fullName,
      Comment: htmlToText(currentIssuesComments[commentValue]!.title),
      [viewPointKey]: {},
    };
    markupJson.Markup[viewPointsKey] = {
      Viewpoint: `${currentIssuesComments[commentValue]!.viewpointKey}.bcfv`,
      Snapshot: `${currentIssuesComments[commentValue]!.snapshotKey}.png`,
    };
  });
  return OBJtoXML(markupJson);
}

// The parameter mirrors DesignViewer's `Record<string, BcfIssueEntry>` verbatim; the richer
// `BcfIssue` view is adopted once per entry below.
export default async function bcfConverter(issues: Record<string, { [key: string]: unknown }>) {
  const zip = new JSZip();
  zip.file(
    "bcf.version",
    '<?xml version="1.0" encoding="UTF-8"?><Version VersionId="2.1" xsi:noNamespaceSchemaLocation="version.xsd" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><DetailedVersion>2.1</DetailedVersion></Version>',
  );
  await Promise.all(
    Object.keys(issues).map(async (val) => {
      // `zip.folder()` is typed `JSZip | null`; every `.file()` below already assumed non-null.
      const issueFolder = zip.folder(val);
      // The caller (DesignViewer) declares its entries as index-signature bags, so the named fields
      // this export reads are adopted once here instead of at every read.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- DesignViewer.tsx:88 types the map as `BcfIssueEntry = { comments?: Record<string, unknown>; [key: string]: unknown }`; the entries are redux `IssueItem`s whose named fields `BcfIssue` mirrors
      const issue = issues[val] as BcfIssue;
      const comments = { ...issue.comments };
      Object.keys(comments).map((commentId) => {
        comments[commentId]!.snapshotKey = uuid();
        comments[commentId]!.bcfDataKey = uuid();
        comments[commentId]!.viewpointKey = uuid();
      });
      const markup = getMarkup(issue, comments);
      issueFolder!.file("markup.bcf", markup);

      const viewpointData = await axiosWrapper.post<IssueViewpointDataResponse>(
        `${getBackendUrl()}/issues/getIssueViewpointData`,
        { id: val },
      );
      if (viewpointData.data.status) {
        const issueViewpoint = getViewpoint(JSON.parse(viewpointData.data.result.bcfData));
        issueFolder!.file("viewpoint.bcfv", issueViewpoint);

        const snapshotImage = await axiosWrapper.post<GetFileUrlResponse>(
          `${getBackendUrl()}/fileSystemServer/getFileUrl`,
          {
            key: issue.snapshot,
            type: "IssueImage",
          },
        );
        // `image` is optional on getFileUrlResponse; the base64 strip below has always assumed it.
        const snapshot = await getSnapshot(snapshotImage.data.image!);
        issueFolder!.file("snapshot.png", snapshot);
      }

      await Promise.all(
        Object.keys(comments).map(async (commentVal) => {
          if (comments[commentVal]!.bcfData) {
            const commentViewpoint = getViewpoint(JSON.parse(comments[commentVal]!.bcfData!));
            issueFolder!.file(`${comments[commentVal]!.viewpointKey}.bcfv`, commentViewpoint);

            // `snapshot` is optional on the stored comment; the strip below has always assumed it.
            const snapshotComment = await getSnapshot(comments[commentVal]!.snapshot!);
            issueFolder!.file(`${comments[commentVal]!.snapshotKey}.png`, snapshotComment);
          }
        }),
      );
    }),
  );

  zip
    .generateAsync({
      type: "blob",
    })
    .then((data) => {
      downloadBcf(data, "cube.bcfzip");
    });
}
